import React, {
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import geodist from 'geodist';
import UserHollowSVG from '../../search/admin-dashboard-tables/assets/UserHollowSVG';
import CompassSVG from '../../svgComponents/CompassSVG';
import { LocationParams } from '../../search/admin-dashboard-tables/interfaces';
import fetchSearchParams from '../../../utils/fetchSearchParams';
import { BREEDER_SELLER_DEFAULT_IMAGE } from '../../../utils/constants';
import { setFallbackImage } from '../../../utils/imageHelper';
import { cloudinaryTransform } from '../../../utils/cloudinaryTransformations';

const BreederCard: React.FC<any> = (data: any): ReactElement => {
  const { breeder } = data;

  const [distance, setDistance] = useState<number|null>(null);
  const [lat, setLat] = useState<string|null>('');
  const [lon, setLon] = useState<string|null>('');

  const verified = breeder.tier === 'verified';
  const classNames = verified ? 'verified breeder-card' : 'breeder-card';

  const updateCoordinates = useCallback(() => {
    const initialLat = localStorage.getItem('latitude');
    const initialLon = localStorage.getItem('longitude');
    if (lat !== initialLat) {
      setLat(initialLat);
    }
    if (lon !== initialLon) {
      setLon(initialLon);
    }
  }, [lat, lon]);

  const updateLocation = useCallback((e: any) => {
    const { detail } = e as { detail: { newLocation: boolean } };
    if (detail && detail.newLocation) {
      updateCoordinates();
    }
  }, [updateCoordinates]);

  useEffect(() => {
    const { lat: latQuery, lng: lonQuery }: LocationParams = fetchSearchParams();
    if (latQuery && lonQuery) {
      setLat(latQuery);
      setLon(lonQuery);
    } else {
      updateCoordinates();
    }
  }, []);

  useEffect(() => {
    document.addEventListener('updateLocation', updateLocation);
    return () => document.removeEventListener('updateLocation', updateLocation);
  }, [updateLocation]);

  useEffect(() => {
    if (lat && lon) {
      let tempDist = null;
      // eslint-disable-next-line no-underscore-dangle
      const breederLoc = breeder?._geoloc
        || { lat: breeder?.location?.lat, lng: breeder?.location?.lng };
      if (breederLoc?.lat && breederLoc?.lng) {
        tempDist = geodist(
          {
            lat: breederLoc.lat,
            lon: breederLoc.lng,
          },
          {
            lat,
            lon,
          },
          {
            unit: 'miles',
          },
        );
        if (tempDist !== distance) {
          setDistance(tempDist);
        }
      }
    }
  }, [distance, breeder, lat, lon]);

  return (
    <>
      <div key={breeder.id} className={classNames}>
        <a href={`${breeder.route}`}>
          <div className="position-relative card-img-top rounded">
            {
              breeder.profile_photo_url
                ? (
                  <LazyLoadImage
                    className="rounded"
                    src={cloudinaryTransform(
                      breeder.profile_photo_url,
                      [
                        'w_280',
                      ],
                    )}
                    onError={(e) => {
                      setFallbackImage(e, BREEDER_SELLER_DEFAULT_IMAGE);
                    }}
                    alt={breeder.name}
                  />
                ) : (
                  <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                    <UserHollowSVG
                      style={{
                        width: '32px',
                        height: '32px',
                      }}
                    />
                  </div>
                )
            }
          </div>
          <div className="breeder-info-container">
            <div className="top-container d-flex justify-content-between">
              <div className="top-left-container d-flex justify-content-left">
                <p className="mb-0"><strong>{breeder.name}</strong></p>
              </div>
              {verified && (
                <span className="badge badge-lg badge-yellow my-auto badge-pill">
                  <i className="fas fa-award fa-xs mr-2" />
                  <strong>
                    Accredited Seller
                  </strong>
                </span>
              )}
            </div>

            <div>
              <small>
                <p className="breeder-description m-0 text-muted">
                  {breeder.breed_name}
                </p>
              </small>
            </div>

            <div className="d-flex">
              {typeof distance === 'number' && (
                <span className="d-flex align-items-center mr-2">
                  <CompassSVG />
                  <span className="ml-1 distance-text">
                    {`${distance} mi`}
                  </span>
                </span>
              )}
              {(breeder.city != null && breeder.state != null) && (
                <span className="badge badge-lg badge-l-gray my-auto badge-pill">
                  {`${breeder.city}, ${breeder.state}`}
                </span>
              )}
            </div>
          </div>
        </a>
      </div>
    </>
  );
};

export default BreederCard;
